// @flow

import * as React from 'react';
import { useState } from 'react';

import { useDispatch } from 'react-redux';

import {
  selectOneProductErrors,
  getCart,
  updateCartProduct,
  type DecoratedProduct,
  webhosting,
} from '@gandi/cart';
import { useTranslator } from '@gandi/translate';

import { selectOrdersListUrl } from 'src/SimpleHosting/modules/urls';
import { useNotifications } from 'src/hooks';
import { useAppSelector } from 'src/hooks/useAppSelector';

import { PaasAddDiskDetails } from './components/PaasAddDiskDetails';
import ProductErrorItem from './components/ProductErrorItem';
import ProductItem from './components/ProductItem';
import { CountRow, OperationAlreadyExistsErrorRow } from './components/Rows';
import { WebHostingSizeToggleGroupRow } from './components/Rows/WebHostingSizeToggleGroupRow';

export type Props = {
  product: DecoratedProduct,
};

/**
 * PaasUpdate has to handle
 * legacy and webhosting updates
 *
 * For webhosting it can only handle one operation at the time:
 * - change billing plan
 * - upgrade the hosting machine
 * - add some disk storage
 *
 * For legacy we do not purpose to change the options at this step
 */
export function PaasUpdate({ product }: Props): React.MixedElement {
  const { __ } = useTranslator();

  const dispatch = useDispatch();
  const { notifyMaybeApiError } = useNotifications();
  const [isUpdatingSize, setIsUpdatingSize] = useState(false);
  const [isUpdatingAddDisk, setIsUpdatingAddDisk] = useState(false);

  const isBusy = isUpdatingSize || isUpdatingAddDisk;

  const productId = product.id;
  const productIdProps = React.useMemo(() => ({ productId }), [productId]);
  const productErrors = useAppSelector(state =>
    selectOneProductErrors(state, productIdProps),
  );

  const ordersListUrl = useAppSelector(selectOrdersListUrl);

  const label = __('Web Hosting');

  const subprocess = webhosting.getSubprocess(product.payload, product.prices);
  const updateLabel =
    subprocess === 'resize'
      ? webhosting.useResizeDescription(product.payload)
      : __('Add extra storage');
  const detail = ['add_disk', 'resize'].includes(subprocess)
    ? updateLabel
    : product.payload.subProcessDescription || __('Update');
  const isWebhosting = webhosting.getIsWebhosting(product.payload);

  /// size change
  const updateSizeHandler = async (evt: SyntheticInputEvent<>) => {
    evt.preventDefault();

    // use an internal flag to avoid brutal price switching
    setIsUpdatingSize(true);

    try {
      await dispatch(
        updateCartProduct({
          productId,
          // $FlowFixMe[incompatible-call]  value is set by options
          payload: { ...product.payload, size: evt.target.value },
        }),
      );

      await dispatch(getCart());
    } catch (e) {
      notifyMaybeApiError(e);
    }

    setIsUpdatingSize(false);
  };

  /// add disk change
  const updateAddDiskHandler = async (count: number) => {
    setIsUpdatingAddDisk(true);

    try {
      await dispatch(
        updateCartProduct({
          productId,
          payload: {
            ...product.payload,
            data_disk_additional_size: webhosting.getDataDiskAdditionalSize(
              product.payload,
              count,
            ),
          },
        }),
      );
      await dispatch(getCart());
    } catch (e) {
      notifyMaybeApiError(e);
    }
    setIsUpdatingAddDisk(false);
  };

  /// render error
  if (productErrors.length > 0) {
    const hasOperationAlreadyExistsError = productErrors.some(
      error => error.location === 'path' && error.name === 'instance',
    );

    return (
      <ProductErrorItem
        key={`${product.id}-error-row`}
        productId={productId}
        label={label}
        detail={detail}
        errors={productErrors}
      >
        {hasOperationAlreadyExistsError ? (
          <OperationAlreadyExistsErrorRow ordersListUrl={ordersListUrl} />
        ) : null}
      </ProductErrorItem>
    );
  }

  const addedStorage = webhosting.getAddedStorage(product.payload);
  const isValidAddDiskPayload =
    !!product.payload.shopping &&
    Number.isFinite(product.payload.shopping.baseStorage) &&
    // $FlowFixMe[incompatible-use] shopping is tested before
    Number.isFinite(product.payload.shopping.additionalStorage);

  /// render product
  return (
    <ProductItem
      key={`${productId}-row`}
      hasTaxes={product.hasTaxes}
      priceBeforeTaxes={
        product.isWorking ? undefined : product.total?.before_taxes
      }
      productId={productId}
      label={label}
      detail={detail}
      header={
        <>
          {isWebhosting && subprocess === 'resize' && (
            <WebHostingSizeToggleGroupRow
              isBusy={isBusy}
              product={product}
              updateSizeHandler={updateSizeHandler}
              removeLowerThanActual
            />
          )}

          {subprocess === 'add_disk' &&
            addedStorage !== undefined &&
            isValidAddDiskPayload && (
              <CountRow
                isLoading={false}
                productId={productId}
                onChange={updateAddDiskHandler}
                count={addedStorage}
                label={__('Increase the storage (up to 1 TB)')}
                detail={
                  <PaasAddDiskDetails
                    addedStorage={addedStorage}
                    additionalStorage={
                      product.payload.shopping?.additionalStorage
                    }
                    baseStorage={product.payload.shopping?.baseStorage}
                  />
                }
              />
            )}
        </>
      }
    />
  );
}
